import {
	ResponsiveContainer,
	ComposedChart,
	XAxis,
	YAxis,
	Bar,
	Line,
	LabelList,
	Tooltip,
	BarChart,
	Legend,
	ReferenceLine,
	Scatter,
} from "recharts";
import { numberFormatter } from "../../utils";
import moment from "moment";

interface Props {
	chartData: any;
	heading: any;
	page: any;
	isVisible: any;
}

interface T {
	quarter: string;
	actual: number;
	forecastedSpend: number;
	spendNeededForNextTier: number;
	spendShift: number;
	targetGrowthSpend: number;
}

const CustomLabel = (props: any) => {
	const { x, y, value, index } = props.payload;
	const { page, dataLength, data } = props;
	const shouldShow = index === 0 || index === dataLength - 1;
	const offset = 20;
	if (!shouldShow) return null;
	const correspondingPoint = data[index];
	const gmCurValue = correspondingPoint?.gmCur;
	const isGreater = value > gmCurValue;
	return (
		<g>
			<line
				x1={x}
				y1={y}
				x2={x}
				y2={isGreater ? y - offset - 10 : y + offset + 10}
				stroke="#0097a9"
				opacity={0.5}
			/>
			<text
				x={x - offset + 8}
				y={isGreater ? y - offset - 10 : y + offset + 15}
				fill="#0097a9"
				className="graph-label-small"
				opacity={`${page == "summary" ? 0.8 : 1}`}
			>
				{numberFormatter(value, 1) + "%"}
			</text>
		</g>
	);
};

const CustomLabel2 = (props: any) => {
	const { x, y, value, index } = props.payload;
	const { page, dataLength, data } = props;
	const shouldShow = index === 0 || index === dataLength - 1;
	const offset = 20;
	if (!shouldShow) return null;
	const correspondingPoint = data[index];
	const gmPrevValue = correspondingPoint?.gmPrev;
	const isGreater = value > gmPrevValue;
	return (
		<g>
			<line
				x1={x}
				y1={y}
				x2={x}
				y2={isGreater ? y - offset - 10 : y + offset + 5}
				stroke="#86bc25"
				opacity={0.5}
			/>
			<text
				x={x - offset + 5}
				y={isGreater ? y - offset - 10 : y + offset + 15}
				fill="#86bc25"
				className="graph-label-small"
				opacity={`${page == "summary" ? 0.8 : 1}`}
			>
				{numberFormatter(value, 1) + "%"}
			</text>
		</g>
	);
};

const MarginImprovement = (props: Props) => {
	let data: any = props?.chartData?.finalData;
	let avgReductionValue: any = props?.chartData?.averageReduction;
	const { page } = props;
	let newData: any = [];
	if (data?.length > 0) {
		data?.map((item: any, index: any) => {
			const finalFormattedDate = moment.utc(item.month).format("MMM");
			if (item.monthCovered) {
				newData.push({
					date: finalFormattedDate,
					gmPrev: item.margin_Prev * 100,
					gmCur: item.margin_Curr * 100,
				});
			}
		});
	}
	let heading = props?.heading;

	const footerData = [{ category: "2025" }, { category: "2026" }];

	const updateFill = (category: string) => {
		switch (category) {
			case "2025":
				return "#0097a9";
			case "2026":
				return "#86bc25";
			default:
				return "black";
		}
	};

	const updateLegend = (category: string) => {
		return category;
	};

	const customTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;

			const popupStyle: any = {
				width: "12vw",
				background: "white",
				border: "1px solid #ccc",
				boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
				borderRadius: "1vh",
				cursor: "pointer",
				color: "black",
			};

			return (
				<div style={popupStyle}>
					{/* Header Row */}
					<div className="w-full flex justify-between h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
						<div className="font-bold uppercase">Month</div>
						<div className="font-bold" style={{ color: "#3f3f3f" }}>
							{data.date}
						</div>
					</div>
					{/* Data Rows */}
					<div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Gross Margin 2026:{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.gmCur, 2)}%
							</label>
						</p>
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Gross Margin 2025:{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.gmPrev, 2)}%
							</label>
						</p>
					</div>
				</div>
			);
		}
		return null;
	};

	return (
		<div className="w-full h-full px-3">
			{/* <div className="headSpace pb-2 !h-[10%]">
				<div className="headContent min-w-[12vw]">Gross Margin</div>
				<span
					className="bg-[#4d4d4d] w-full h-[0] flex justify-end px-[2vh] font-semibold text-[1.8vh]"
					style={{
						color:
							avgReductionValue?.bps > 0 ? "#40a108" : "#a10808",
					}}
				>
					{numberFormatter(avgReductionValue?.bps, 1)}bps Margin{" "}
					{avgReductionValue?.bps > 0 ? "Improvement" : "Drop"}
				</span>{" "}
			</div> */}
			<div className="headSpace !h-[6vh]">
				<span className="headContent"> Gross Margin</span>
				<p
					className="flex justify-end px-[2vh] font-semibold textNormal mt-[0] text-[#40a108]"
					style={{
						color:
							avgReductionValue?.bps > 0 ? "#40a108" : "#a10808",
					}}
				>
					{numberFormatter(avgReductionValue?.bps, 1)}bps Margin{" "}
					{avgReductionValue?.bps > 0 ? "Improvement" : "Drop"}
				</p>
			</div>
			<ResponsiveContainer
				width="100%"
				height={props.isVisible ? "69%" : "77%"}
				className="bg-[#ffff] px-[1vh] "
			>
				<ComposedChart
					data={newData}
					margin={{ top: 20, right: 25, left: 20 }}
				>
					<XAxis
						dataKey="date"
						className="graph-label"
						stroke="black"
						interval={0}
					/>
					<YAxis
						hide
						fontSize="1.5vh"
						tickFormatter={(value: number) =>
							numberFormatter(value, 0, "$")
						}
						domain={["dataMin - 5", "dataMax + 5"]}
					/>
					<Tooltip
						contentStyle={{
							color: "white",
							backgroundColor: "rgb(26, 25, 22)",
						}}
						cursor={{ strokeDasharray: "3 3", fill: "#212121" }}
						content={customTooltip}
					/>
					<ReferenceLine
						y={0}
						label={{
							position: "insideTopRight",
							fill: "#0097a9",
							fontStyle: "italic",
							fontSize: "1.8vh",
							value: numberFormatter(0, 2) + "%",
						}}
						stroke="#0097a9"
						strokeDasharray="5 3"
						opacity={1}
					/>
					<ReferenceLine
						y={0}
						label={{
							position: "insideBottomLeft",
							fill: "#86bc25",
							fontStyle: "italic",
							fontSize: "1.8vh",
							value: numberFormatter(0, 2) + "%",
						}}
						stroke="#86bc25"
						strokeDasharray="5 3"
						opacity={1}
					/>
					<Line
						type="linear"
						dataKey="gmPrev"
						stroke="#0097a9"
						name={`2025`}
						strokeDasharray={"0"}
						connectNulls={true}
						strokeWidth={3}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={newData}
											color={"#0097a9"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="gmPrev"
							content={($) => (
								<CustomLabel
									payload={$}
									page={page}
									dataLength={newData.length}
									data={newData}
								/>
							)}
						/>
					</Line>
					<Line
						type="linear"
						dataKey="gmCur"
						stroke="#86bc25"
						name={`2026`}
						strokeDasharray={"0"}
						connectNulls={true}
						strokeWidth={3}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={newData}
											color={"#86bc25"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="gmCur"
							content={($) => (
								<CustomLabel2
									payload={$}
									page={page}
									dataLength={newData.length}
									data={newData}
								/>
							)}
						/>
					</Line>
				</ComposedChart>
			</ResponsiveContainer>
			<div className="contentFooter">
				<div className="flex flex-wrap items-start gap-x-2  p-4 w-full">
					{footerData.map((entry, index) => (
						<div
							key={`legend-${index}`}
							className="flex items-center min-h-[20px] cursor-pointer"
						>
							<div
								className="w-3 h-3 mr-2"
								style={{
									backgroundColor: updateFill(entry.category),
								}}
							></div>
							<span
								className="text-[0.85em] leading-tight"
								style={{
									color: updateFill(entry.category),
								}}
							>
								{updateLegend(entry.category)}
							</span>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

const CustomDot = (props: any) => {
	const { cx, cy, index, data, color } = props;
	// Only render for first and last point
	if (index === 0 || index === data.length - 1) {
		return <circle cx={cx} cy={cy} r={5} fill={color} stroke="none" />;
	}
	return null;
};

export default MarginImprovement;
